import type { Lang } from "../src/languages";
import {
  alphabet,
  BASE_PATH,
  baseLetter,
  NAME_MAX_LENGTH,
  PLAYER_COLORS,
  rank,
  sanitizeName,
  TURN_MS,
  type ErrorCode,
  type PlayerInfo,
  type PuzzleView,
  type RoomStatus,
  type RoundSummary,
  type ServerMsg,
} from "../src/protocol";
import { track } from "./analytics";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { Figure } from "./figure";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { uiLangSelect } from "./langPicker";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { shareButton, type ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { STRINGS } from "./strings";
import { formatClock } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
const TOAST_MS = 2500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;
/** How often the clock and the turn bar refresh. The server sends no ticks. */
const REFRESH_MS = 100;

/** Room screen. Asks for a name first when the visitor arrived by link without one. */
export function renderRoom(root: HTMLElement, code: string): void {
  const name = sanitizeName(getName());
  if (name === null) renderNamePrompt(root, code);
  else new RoomScreen(root, code, name);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new RoomScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class RoomScreen {
  private readonly connection: Connection;
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly clockEl = h("strong", { class: "clock", "aria-label": STRINGS.timeLeft }, "0:00");
  private readonly headingEl = h("span", { class: "muted" }, STRINGS.timeLeft);
  private readonly figure = new Figure();
  private readonly statusEl = h("p", { class: "stage-status", "aria-live": "polite" });
  private readonly missesEl = h("p", { class: "muted stage-misses" });
  private readonly wrongEl = h("p", { class: "stage-wrong", "aria-hidden": "true" });
  private readonly puzzleEl = h("div", { class: "puzzle", "aria-live": "polite" });
  private readonly turnBarEl = h("div", { class: "turn-bar-fill" });
  private readonly keyboardEl = h("div", { class: "keyboard", role: "group", "aria-label": STRINGS.keyboardLabel });
  private readonly overlayEl = h("div", { class: "arena-overlay" });
  private readonly teamScoreEl = h("strong", { class: "team-score" }, "0");
  private readonly teamStatsEl = h("p", { class: "muted team-stats" });
  private readonly playersEl = h("ol", { class: "player-list" });

  private you = "";
  private status: RoomStatus = "lobby";
  private lang: Lang = "en";
  private players = new Map<string, PlayerInfo>();
  private summary: RoundSummary | null = null;
  /** Round length of the room, for the shared result. */
  private roundSeconds = 0;
  private puzzle: PuzzleView | null = null;
  /** Local times at which the countdown/round and the current turn end. */
  private deadline = 0;
  private turnDeadline = 0;
  private refreshTimer: ReturnType<typeof setInterval> | undefined;
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  private keys = new Map<string, HTMLButtonElement>();
  private keyboardLang: Lang | null = null;
  private fatal = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    const stage = h(
      "div",
      { class: "card stage" },
      h(
        "div",
        { class: "stage-top" },
        this.figure.el,
        h("div", { class: "stage-info" }, this.statusEl, this.missesEl, this.wrongEl),
      ),
      this.puzzleEl,
      h("div", { class: "turn-bar", "aria-hidden": "true" }, this.turnBarEl),
      this.keyboardEl,
      this.overlayEl,
    );

    root.replaceChildren(
      this.header(),
      this.toastEl,
      h(
        "main",
        { class: "arena-layout" },
        h("section", { class: "arena-area" }, h("div", { class: "arena-hud" }, this.headingEl, this.clockEl), stage),
        h(
          "aside",
          { class: "panel" },
          h(
            "section",
            { class: "card team-card" },
            h("h3", { class: "panel-title" }, STRINGS.teamScore),
            this.teamScoreEl,
            this.teamStatsEl,
          ),
          h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.contributions), this.playersEl),
          this.inviteCard(),
        ),
      ),
      siteFooter(),
    );

    window.addEventListener("keydown", (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
      if (document.querySelector("dialog[open]")) return;
      if ([...event.key].length !== 1) return;
      const letter = baseLetter(event.key);
      if (letter !== "" && this.keys.has(letter)) this.guess(letter);
    });

    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exit = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exit.addEventListener("click", () => this.confirmExit());
    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exit,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.toast(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.inviteTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.share);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // The share sheet was dismissed: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.inviteTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  private guess(letter: string): void {
    const puzzle = this.puzzle;
    if (this.status !== "running" || !puzzle || puzzle.phase !== "guess" || puzzle.turn !== this.you) return;
    if (puzzle.hits.includes(letter) || puzzle.wrong.includes(letter)) return;
    this.connection.send({ type: "guess", letter });
  }

  private onStatus(status: ConnectionStatus): void {
    if (this.fatal) return;
    if (status === "connecting") this.toast(STRINGS.connecting, true);
    else if (status === "reconnecting") this.toast(STRINGS.reconnecting, true);
    else if (status === "open") this.hideToast();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previous = this.status;
        this.you = msg.you;
        this.status = msg.status;
        this.roundSeconds = msg.duration;
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "running" && previous !== "running") {
          track("round_started", { people: msg.players.length, duration: msg.duration });
        } else if (msg.status === "finished" && previous === "running") {
          track("round_finished", { people: msg.players.length });
        }
        this.lang = msg.lang;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.summary = msg.summary;
        this.deadline = Date.now() + msg.remainingMs;
        if (msg.status !== "running") this.puzzle = null;
        this.buildKeyboard();
        this.renderStatus();
        this.renderPuzzle();
        this.renderTeam();
        break;
      }
      case "puzzle": {
        const previousTurn = this.puzzle?.turn;
        for (const [slot, score] of msg.p) {
          for (const player of this.players.values()) if (player.slot === slot) player.score = score;
        }
        this.puzzle = msg;
        this.deadline = Date.now() + msg.t;
        this.turnDeadline = Date.now() + msg.turnMs;
        this.renderPuzzle(true);
        this.renderTeam();
        this.refresh();
        // A small nudge when the turn comes round to you.
        if (msg.turn === this.you && previousTurn !== this.you) navigator.vibrate?.(30);
        break;
      }
      case "player_joined":
      case "player_updated":
        this.players.set(msg.player.id, msg.player);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "player_left":
        this.players.delete(msg.id);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "error":
        this.onError(msg.code);
        break;
      case "pong":
        break;
    }
  }

  private onError(code: ErrorCode): void {
    if (code === "BAD_MESSAGE") return;
    this.fatal = true;
    this.connection.stop();
    clearInterval(this.refreshTimer);
    this.root.replaceChildren(
      h(
        "main",
        { class: "center-screen" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h(
          "section",
          { class: "card center-card" },
          h("p", {}, code === "ROOM_FULL" ? STRINGS.errorRoomFull : STRINGS.errorRoomNotFound),
          h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
        ),
      ),
      siteFooter(),
    );
  }

  /** Overlay and timers for the current phase of the room. */
  private renderStatus(): void {
    clearInterval(this.refreshTimer);
    this.refreshTimer = undefined;
    this.overlayEl.hidden = this.status === "running";

    if (this.status === "lobby") {
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.lobbyTitle),
        h("p", {}, STRINGS.lobbyBody(this.onlineCount())),
        this.startButton(STRINGS.startRound),
      );
    } else if (this.status === "countdown") {
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.refreshTimer = setInterval(update, REFRESH_MS);
      this.overlayEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
    } else if (this.status === "finished") {
      this.setClock(0);
      this.overlayEl.replaceChildren(...this.resultsContent());
    } else {
      this.refresh();
      this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS);
    }
  }

  private resultsContent(): HTMLElement[] {
    const summary = this.summary;
    const content: HTMLElement[] = [h("h2", {}, STRINGS.resultsTitle)];
    if (summary) {
      content.push(
        h("p", { class: "results-headline" }, STRINGS.resultsScore(summary.score)),
        h("p", { class: "muted" }, `${STRINGS.solvedCount(summary.solved)} · ${STRINGS.lostCount(summary.lost)}`),
      );
      if (summary.unfinished) content.push(h("p", { class: "muted" }, STRINGS.unfinished(summary.unfinished)));
    }
    content.push(this.startButton(STRINGS.newRound));
    if (summary) content.push(shareButton(() => this.resultCard(summary)));
    return content;
  }

  /** What "Share result" draws: the team's score. */
  private resultCard(summary: RoundSummary): ResultCard {
    return {
      title: "StickBrawl",
      slug: "stickbrawl",
      accent: "#f76707",
      team: {
        score: STRINGS.points(summary.score),
        detail: `${STRINGS.solvedCount(summary.solved)} · ${STRINGS.lostCount(summary.lost)}`,
      },
      people: this.players.size,
      minutes: Math.round(this.roundSeconds / 60),
    };
  }

  private startButton(label: string): HTMLElement {
    const button = h("button", { type: "button", class: "btn btn-primary" }, label);
    button.addEventListener("click", () => this.connection.send({ type: "start" }));
    return button;
  }

  /** One key per letter of the room's puzzle language. Rebuilt only if the language changes. */
  private buildKeyboard(): void {
    if (this.keyboardLang === this.lang) return;
    this.keyboardLang = this.lang;
    this.keys.clear();
    this.keyboardEl.replaceChildren(
      ...alphabet(this.lang).map((letter) => {
        const key = h("button", { type: "button", class: "key" }, letter);
        key.addEventListener("click", () => this.guess(letter));
        this.keys.set(letter, key);
        return key;
      }),
    );
  }

  /** Figure, letter tiles, turn line and keyboard. `animate` marks the letters just revealed. */
  private renderPuzzle(animate = false): void {
    const puzzle = this.puzzle;
    if (!puzzle) {
      this.figure.set(0, 6, STRINGS.figureLabel(0, 6));
      this.puzzleEl.replaceChildren();
      this.statusEl.textContent = "";
      this.missesEl.textContent = "";
      this.wrongEl.textContent = "";
      this.headingEl.textContent = STRINGS.timeLeft;
      this.turnBarEl.style.width = "0";
      for (const key of this.keys.values()) {
        key.disabled = true;
        key.className = "key";
      }
      return;
    }

    const misses = puzzle.wrong.length;
    this.figure.set(misses, puzzle.maxMisses, STRINGS.figureLabel(misses, puzzle.maxMisses));
    this.headingEl.textContent = `${STRINGS.puzzleNumber(puzzle.n)} · ${STRINGS.categories[puzzle.category]}`;

    // Tiles are grouped by word so a line never breaks in the middle of one.
    const justFound = animate && puzzle.last && puzzle.last.found > 0 ? puzzle.last.letter : null;
    const words: HTMLElement[] = [];
    let word = h("span", { class: "puzzle-word" });
    for (const char of puzzle.pattern) {
      if (char === " ") {
        words.push(word);
        word = h("span", { class: "puzzle-word" });
        continue;
      }
      const letter = baseLetter(char);
      const hidden = char === "_";
      const tile = h(
        "span",
        { class: hidden ? "tile tile-hidden" : letter === "" ? "tile tile-mark" : "tile" },
        hidden ? "" : char,
      );
      if (justFound && letter === justFound) tile.classList.add("tile-pop");
      word.append(tile);
    }
    words.push(word);
    this.puzzleEl.replaceChildren(...words);
    this.puzzleEl.classList.toggle("puzzle-solved", puzzle.phase === "solved");
    this.puzzleEl.classList.toggle("puzzle-lost", puzzle.phase === "lost");

    const myTurn = puzzle.phase === "guess" && puzzle.turn === this.you;
    if (puzzle.phase === "solved") this.statusEl.textContent = STRINGS.solvedBanner;
    else if (puzzle.phase === "lost") this.statusEl.textContent = STRINGS.lostBanner;
    else if (myTurn) this.statusEl.textContent = STRINGS.yourTurn;
    else {
      const who = puzzle.turn ? this.players.get(puzzle.turn)?.name : undefined;
      this.statusEl.textContent = who ? STRINGS.turnOf(who) : STRINGS.waitingTurn;
    }
    this.statusEl.className = `stage-status${myTurn ? " is-mine" : ""}${puzzle.phase === "solved" ? " is-solved" : ""}${puzzle.phase === "lost" ? " is-lost" : ""}`;
    this.missesEl.textContent = puzzle.phase === "guess" ? STRINGS.missesLeft(puzzle.maxMisses - misses) : "";
    this.wrongEl.textContent = [...puzzle.wrong].join(" ");

    for (const [letter, key] of this.keys) {
      const hit = puzzle.hits.includes(letter);
      const miss = puzzle.wrong.includes(letter);
      key.className = `key${hit ? " key-hit" : ""}${miss ? " key-miss" : ""}`;
      key.disabled = hit || miss || !myTurn;
    }
    this.keyboardEl.classList.toggle("keyboard-active", myTurn);
  }

  /** Team totals and what each person added, best first, with whose turn it is. */
  private renderTeam(): void {
    const puzzle = this.puzzle;
    const finished = this.status === "finished" ? this.summary : null;
    const score = puzzle ? puzzle.score : finished ? finished.score : 0;
    const solved = puzzle ? puzzle.solved : finished ? finished.solved : 0;
    const lost = puzzle ? puzzle.lost : finished ? finished.lost : 0;
    this.teamScoreEl.textContent = String(score);
    this.teamStatsEl.textContent = `${STRINGS.solvedCount(solved)} · ${STRINGS.lostCount(lost)}`;

    const turn = puzzle?.phase === "guess" ? puzzle.turn : null;
    this.playersEl.replaceChildren(
      ...rank([...this.players.values()]).map((p) => {
        const row = h(
          "li",
          { class: `player${p.online ? "" : " player-offline"}${p.id === turn ? " player-turn" : ""}` },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === this.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag" }, STRINGS.offline),
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
        );
        row.style.setProperty("--player", PLAYER_COLORS[p.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  /** Round clock and turn bar, from the local deadlines. */
  private refresh(): void {
    const now = Date.now();
    this.setClock(this.deadline - now);
    const guessing = this.puzzle?.phase === "guess";
    const left = guessing ? Math.min(1, Math.max(0, (this.turnDeadline - now) / TURN_MS)) : 0;
    this.turnBarEl.style.width = `${left * 100}%`;
    this.turnBarEl.classList.toggle("is-mine", guessing && this.puzzle?.turn === this.you);
    this.turnBarEl.classList.toggle("is-low", guessing && left < 0.3);
  }

  private onlineCount(): number {
    return [...this.players.values()].filter((p) => p.online).length;
  }

  private setClock(ms: number): void {
    this.clockEl.textContent = formatClock(ms);
    const running = this.status === "running";
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    // Hue 135 (green) → 0 (red), passing through yellow and orange on the way.
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private toast(text: string, sticky = false): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    if (!sticky) this.toastTimer = setTimeout(() => this.hideToast(), TOAST_MS);
  }

  private hideToast(): void {
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = true;
  }
}

/** Clipboard API where available, with the legacy selection path as a fallback. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}
